import { useEffect, useState } from "react";
import axios from "axios";
import { Target, Printer } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { buildQuizPDF } from "@/utils/quizPdf";
import { useTranslation } from "@/contexts/LanguageContext";
import { getLessonQuiz } from "@/data/quizzes";
import { LEARNING_PATH } from "@/data/learningPath";

export default function ClassWeakSpots({ classId }) {
  const { t, lang } = useTranslation();
  const es = lang === "es";
  const [data, setData] = useState(null);

  useEffect(() => {
    axios
      .get("/api/admin/class-weak-spots", { params: classId ? { class_id: classId } : {}, withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => setData({ students: 0, lessons: [], questions: [] }));
  }, [classId]);

  const printReview = async () => {
    const qs = data.questions.map((q) => getLessonQuiz(q.path, lang)?.[q.i]).filter(Boolean);
    const date = new Date().toISOString().slice(0, 10);
    await buildQuizPDF(qs, es ? "Cuestionario de repaso · preguntas más falladas" : "Review quiz · most-missed questions", `dslr-advisor-review-quiz-${date}-${lang}.pdf`, lang);
  };

  if (!data) return null;
  const name = (p) => {
    const l = LEARNING_PATH.find((x) => x.to === p);
    return l ? t(l.labelKey) : p;
  };
  const max = data.lessons[0]?.missed || 1;

  return (
    <section className="bg-[#121214]/80 border border-white/10 rounded-xl p-4 sm:p-5 space-y-4" data-testid="class-weak-spots">
      <div className="flex items-start justify-between gap-3 flex-wrap">
      <div>
        <h2 className="flex items-center gap-2 font-['Outfit'] text-white text-lg font-semibold">
          <Target weight="fill" className="w-5 h-5 text-[#F5A524]" />
          {es ? "Puntos débiles de la clase" : "Class weak spots"}
        </h2>
        <p className="text-[#71717A] text-sm" data-testid="class-weak-students">
          {es
            ? `Según el último examen de cada estudiante (${data.students} ${data.students === 1 ? "estudiante" : "estudiantes"}). Lo que más se falla, primero.`
            : `Based on each student's latest exam (${data.students} ${data.students === 1 ? "student" : "students"}). Most-missed first.`}
        </p>
      </div>
      {data.questions.length > 0 && (
        <Button size="sm" onClick={printReview} className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium" data-testid="class-weak-print-btn">
          <Printer weight="bold" className="w-4 h-4 mr-1.5" />
          {es ? `Imprimir cuestionario de repaso (${data.questions.length})` : `Print review quiz (${data.questions.length})`}
        </Button>
      )}
      </div>

      {data.lessons.length === 0 ? (
        <p className="text-[#71717A] text-sm" data-testid="class-weak-empty">
          {es ? "Aún no hay exámenes con datos de preguntas falladas." : "No exams with missed-question data yet."}
        </p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="space-y-2" data-testid="class-weak-lessons">
            <p className="text-[#A1A1AA] text-xs uppercase tracking-[0.18em]">{es ? "Lecciones para repasar" : "Lessons to reteach"}</p>
            {data.lessons.slice(0, 10).map((l) => (
              <div key={l.path} className="space-y-1" data-testid={`class-weak-lesson-${l.path.replace(/\//g, "")}`}>
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="text-white truncate">{name(l.path)}</span>
                  <span className="font-['JetBrains_Mono'] text-xs text-[#A1A1AA] shrink-0">
                    {l.missed} {es ? "falladas" : "missed"} · {l.students} {es ? "est." : l.students === 1 ? "student" : "students"}
                  </span>
                </div>
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-red-400/70 rounded-full" style={{ width: `${(l.missed / max) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
          <div className="space-y-2" data-testid="class-weak-questions">
            <p className="text-[#A1A1AA] text-xs uppercase tracking-[0.18em]">{es ? "Preguntas más falladas" : "Most-missed questions"}</p>
            {data.questions.map((q) => {
              const item = getLessonQuiz(q.path, lang)?.[q.i];
              if (!item) return null;
              return (
                <div key={`${q.path}-${q.i}`} className="bg-[#0A0A0A] border border-white/5 rounded-lg px-3 py-2">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-white text-sm">{item.q}</p>
                    <span className="font-['JetBrains_Mono'] text-xs text-red-300 shrink-0">×{q.count}</span>
                  </div>
                  <p className="text-[#71717A] text-xs mt-1">
                    {name(q.path)} · <span className="text-emerald-300">{item.o[item.a]}</span>
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
